import React, { FC, useState, useMemo, memo, useCallback, useEffect } from 'react';
import {
    TextField, Grid, Box, Checkbox,
    FormControlLabel, Typography, Divider, Autocomplete,
    CircularProgress, Chip,
    InputAdornment
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Controller, UseFormReturn } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { nameValidation } from 'utils/Validations'
import ListTransfer from 'components/transfer/ListTransfer';
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import { AvailableData, LoadingState } from './hooks/useAccrualRuleData';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { AccrualRuleRGrantCreate } from 'repository/timekeeper/@types/AccrualRule';
import { AccrualRuleRLimitCreate } from 'repository/timekeeper/@types/AccrualRule';
import dayjs from 'dayjs';

export interface AccrualRuleFormData {
    name: string;
    description: string | null;
    adjustBalance: boolean;
    accrualCodeId: number | '';
    accrualSurplusId: number | null;
    referenceDateServiceId: number | null;
}

//interface Grants { grant_id: number; grant_amount: number; }
//interface Limits { limit_id: number; limit_amount: number | string; limit_amount_unit: string | null; }
interface UseBalances { paycode_id: number; }

interface GrantRow extends AccrualRuleRGrantCreate { id: number; _index: number; }
interface LimitRow extends AccrualRuleRLimitCreate { id: number; _index: number; }

interface AccrualRuleFormProps {
    form: UseFormReturn<AccrualRuleFormData>;
    relationsData: {
        grants: AccrualRuleRGrantCreate[];
        limits: AccrualRuleRLimitCreate[];
        useBalances: UseBalances[];
    };
    setRelationsData: React.Dispatch<React.SetStateAction<{
        grants: AccrualRuleRGrantCreate[];
        limits: AccrualRuleRLimitCreate[];
        useBalances: UseBalances[];
    }>>;
    availableData: AvailableData;
    loadingStates: LoadingState;
    existName: (name: string) => boolean;
    setUseBalances: (useBalances: UseBalances[]) => void;
    accrualRuleId?: number;
}

// ─────────────────────────────────────────────────────────────
// Cell Components
// ─────────────────────────────────────────────────────────────

type GrantCellProps = {
    rowIndex: number;
    control: Control<AccrualRuleFormData>;
    loading: boolean;
    grants: any[];
    t: any;
    relationsData: { grants: AccrualRuleRGrantCreate[]; limits: AccrualRuleRLimitCreate[]; useBalances: UseBalances[]; };
    setRelationsData: React.Dispatch<React.SetStateAction<{ grants: AccrualRuleRGrantCreate[]; limits: AccrualRuleRLimitCreate[]; useBalances: UseBalances[]; }>>;
};

const GrantSelectCell = memo<GrantCellProps>(
    ({ rowIndex, loading, grants, t, relationsData, setRelationsData }) => {
        const currentGrant = relationsData.grants[rowIndex];
        return (

            <Autocomplete
                sx={{ paddingTop: '15px' }}
                fullWidth
                options={grants}
                getOptionLabel={(option) => option.name}
                value={grants.find(grant => grant.grant_id === currentGrant?.grant_id) || null}
                onChange={(event: any, newValue) => {
                    const newGrants = [...relationsData.grants];
                    newGrants[rowIndex] = { ...newGrants[rowIndex], grant_id: newValue ? newValue.grant_id : 0 };
                    setRelationsData(prev => ({ ...prev, grants: newGrants }));
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('accruals.accrual_rules.dialog.sections.grants')}
                        variant="outlined"
                        size="small"
                        slotProps={{
                            input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                                </>
                            ),
                            },
                        }}
                    />
                )}
            />
        );
    },
);

type LimitCellProps = {
    rowIndex: number;
    control: Control<AccrualRuleFormData>;
    loading: boolean;
    limits: any[];
    t: any;
    relationsData: { grants: AccrualRuleRGrantCreate[]; limits: AccrualRuleRLimitCreate[]; useBalances: UseBalances[]; };
    setRelationsData: React.Dispatch<React.SetStateAction<{ grants: AccrualRuleRGrantCreate[]; limits: AccrualRuleRLimitCreate[]; useBalances: UseBalances[]; }>>;
};

const LimitSelectCell = memo<LimitCellProps>(
    ({ rowIndex, loading, limits, t, relationsData, setRelationsData }) => {
        const currentLimit = relationsData.limits[rowIndex];
        return (
            <Autocomplete
                sx={{ paddingTop: '15px' }}
                fullWidth
                options={limits}
                getOptionLabel={(option) => option.name}
                value={limits.find(limit => limit.limit_id === currentLimit?.limit_id) || null}
                onChange={(event, newValue) => {
                    const newLimits = [...relationsData.limits];
                    newLimits[rowIndex] = { ...newLimits[rowIndex], limit_id: newValue?.limit_id || 0 };
                    setRelationsData(prev => ({ ...prev, limits: newLimits }));
                }}
                disabled={loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('accruals.accrual_rules.dialog.sections.limits')}
                        variant="outlined"
                        size="small"
                    />
                )}
            />
        );
    },
);

type AmountCellProps = {
    rowIndex: number;
    type: 'grant' | 'limit';
    t: any;
    relationsData: { grants: AccrualRuleRGrantCreate[]; limits: AccrualRuleRLimitCreate[]; useBalances: UseBalances[]; };
    setRelationsData: React.Dispatch<React.SetStateAction<{ grants: AccrualRuleRGrantCreate[]; limits: AccrualRuleRLimitCreate[]; useBalances: UseBalances[]; }>>;
    unit?: string | null;
};

const AmountCell = memo<AmountCellProps>(
    ({ rowIndex, type, t, relationsData, setRelationsData, unit }) => {
        if (type === 'grant') {
            const currentAmount = relationsData.grants[rowIndex]?.grant_amount || 0;
            return (
                <TextField
                    fullWidth
                    type="number"
                    size="small"
                    label={t('accruals.accrual_rules.dialog.inputs.amount')}
                    value={currentAmount}
                    onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        const newGrants = [...relationsData.grants];
                        newGrants[rowIndex] = { ...newGrants[rowIndex], grant_amount: isNaN(value) ? 0 : value };
                        setRelationsData(prev => ({ ...prev, grants: newGrants }));
                    }}
                    slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                />
            );
        }

        // type === 'limit'
        const currentAmount = relationsData.limits[rowIndex]?.limit_amount;

        if (unit === 'HORAS') {
            const rawTimeValue = typeof currentAmount === 'string' ? currentAmount : '';
            const normalizedTimeValue = rawTimeValue
                ? rawTimeValue.replace(/^(\d{1,2}:\d{2})(?::\d{2})?$/, '$1')
                : '';

            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                        label={t('accruals.accrual_rules.dialog.inputs.amount')}
                        value={normalizedTimeValue ? dayjs(`2022-01-01T${normalizedTimeValue}`) : null}
                        onChange={(newValue) => {
                            const formatted = newValue ? newValue.format("HH:mm") : "";

                            const newLimits = [...relationsData.limits];
                            newLimits[rowIndex] = {
                                ...newLimits[rowIndex],
                                limit_amount: formatted
                            };

                            setRelationsData(prev => ({ ...prev, limits: newLimits }));
                        }}
                        slotProps={{
                            textField: { size: 'small', fullWidth: true },
                        }}
                    />
                </LocalizationProvider>
            );
        }

        // DIAS u otras unidades
        const numericAmount = typeof currentAmount === 'number' ? currentAmount : 0;

        return (
            <TextField
                fullWidth
                type="number"
                size="small"
                label={t('accruals.accrual_rules.dialog.inputs.amount')}
                value={numericAmount}
                onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    const newLimits = [...relationsData.limits];
                    newLimits[rowIndex] = { ...newLimits[rowIndex], limit_amount: isNaN(value) ? 0 : value };
                    setRelationsData(prev => ({ ...prev, limits: newLimits }));
                }}
                slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            />
        );
    },
);

export const AccrualRuleForm: FC<AccrualRuleFormProps> = ({
    form,
    relationsData,
    setRelationsData,
    availableData,
    loadingStates,
    existName,
    setUseBalances,
    accrualRuleId,
}) => {
    const { t } = useTranslation();
    const { control, formState: { errors }, watch } = form;
    const [showExistsError, setShowExistsError] = useState(false);
    const nameValue = watch('name');
    const accrualCodeIdValue = watch('accrualCodeId');
    const theme = useTheme();

    const selectedAccrualCode = useMemo(
        () => availableData.accrualCodes.find(code => code.accrual_code_id === accrualCodeIdValue) || null,
        [availableData.accrualCodes, accrualCodeIdValue],
    );
    const selectedTypeAccrual = selectedAccrualCode?.type_accrual ?? null;

    useEffect(() => {
        setRelationsData(prev => ({
            ...prev,
            limits: prev.limits.map(limit => ({
                ...limit,
                limit_amount_unit: selectedTypeAccrual,
            })),
        }));
    }, [selectedTypeAccrual, setRelationsData]);

    // ── Funciones para gestionar grants ──
    const addGrant = useCallback(() => {
        const newGrant: any = { grant_id: 0, grant_amount: 0 };
        if (accrualRuleId) {
            newGrant.accrual_rule_id = accrualRuleId;
        }
        setRelationsData(prev => ({ ...prev, grants: [...prev.grants, newGrant] }));
    }, [setRelationsData, accrualRuleId]);

    const removeGrant = useCallback((index: number) => {
        setRelationsData(prev => ({
            ...prev,
            grants: prev.grants.filter((_, i) => i !== index)
        }));
    }, [setRelationsData]);

    // ── Funciones para gestionar limits ──
    const addLimit = useCallback(() => {
        const newLimit: any = {
            limit_id: 0,
            limit_amount: 0,
            limit_amount_unit: selectedTypeAccrual ?? '',
        };
        if (accrualRuleId) {
            newLimit.accrual_rule_id = accrualRuleId;
        }
        setRelationsData(prev => ({ ...prev, limits: [...prev.limits, newLimit] }));
    }, [setRelationsData, accrualRuleId, selectedTypeAccrual]);

    const removeLimit = useCallback((index: number) => {
        setRelationsData(prev => ({
            ...prev,
            limits: prev.limits.filter((_, i) => i !== index)
        }));
    }, [setRelationsData]);

    // ── Filas para DataGrid ──
    const grantRows: GrantRow[] = useMemo(
        () => relationsData.grants.map((grant, index) => ({
            ...grant,
            id: index,
            _index: index,
        })),
        [relationsData.grants],
    );

    const limitRows: LimitRow[] = useMemo(
        () => relationsData.limits.map((limit, index) => ({
            ...limit,
            id: index,
            _index: index,
        })),
        [relationsData.limits],
    );

    // ── Columnas para Grants DataGrid ──
    const grantColumns: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                headerName: t('accruals.accrual_rules.dialog.sections.actions'),
                headerClassName: 'super-app-theme--header',
                flex: 0.5,
                headerAlign: 'center',
                align: 'center',
                getActions: (params: any) => {
                    const rowIndex: number = params.row._index;
                    const isLastRow = rowIndex === grantRows.length - 1;
                    const isFirstRow = rowIndex === 0;

                    const actions: React.ReactElement[] = [];

                    if (!(isFirstRow && isLastRow)) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon color="error" />}
                                label={t('general.buttons.delete')}
                                onClick={() => removeGrant(rowIndex)}
                                disabled={loadingStates.grants}
                                showInMenu={false}
                            />
                        );
                    }

                    if (isLastRow) {
                        actions.push(
                            <GridActionsCellItem
                                key="add"
                                icon={<AddIcon color="action" />}
                                label={t('general.buttons.add')}
                                onClick={addGrant}
                                showInMenu={false}
                            />
                        );
                    }

                    return actions;
                },
                resizable: false, filterable: false, sortable: false,
                editable: false, groupable: false,
            },
            {
                field: 'grant_id',
                headerName: t('accruals.accrual_rules.dialog.sections.grants'),
                headerClassName: 'super-app-theme--header',
                flex: 1.5,
                align: 'center',
                renderCell: (params: any) => (
                    <GrantSelectCell
                        rowIndex={params.row._index}
                        control={control}
                        loading={loadingStates.grants}
                        grants={availableData.grants}
                        t={t}
                        relationsData={relationsData}
                        setRelationsData={setRelationsData}
                    />
                ),
                resizable: false, filterable: false, sortable: false,
                editable: false, groupable: false,
            },
            {
                field: 'grant_amount',
                headerName: t('accruals.accrual_rules.dialog.inputs.amount'),
                headerClassName: 'super-app-theme--header',
                flex: 1,
                align: 'center',
                renderCell: (params: any) => (
                    <AmountCell
                        rowIndex={params.row._index}
                        type="grant"
                        t={t}
                        relationsData={relationsData}
                        setRelationsData={setRelationsData}
                        unit={null}
                    />
                ),
                resizable: false, filterable: false, sortable: false,
                editable: false, groupable: false,
            },
        ],
        [t, availableData.grants, grantRows.length, control, loadingStates.grants, relationsData, setRelationsData, addGrant, removeGrant],
    );

    // ── Columnas para Limits DataGrid ──
    const limitColumns: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                headerName: t('accruals.accrual_rules.dialog.sections.actions'),
                headerClassName: 'super-app-theme--header',
                flex: 0.5,
                headerAlign: 'center',
                align: 'center',
                getActions: (params: any) => {
                    const rowIndex: number = params.row._index;
                    const isLastRow = rowIndex === limitRows.length - 1;
                    const isFirstRow = rowIndex === 0;

                    const actions: React.ReactElement[] = [];

                    if (!(isFirstRow && isLastRow)) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon color='error'/>}
                                label={t('general.buttons.delete')}
                                onClick={() => removeLimit(rowIndex)}
                                disabled={loadingStates.limits}
                                showInMenu={false}
                                sx={{ backgroundColor: 'transparent' }}
                            />
                        );
                    }

                    if (isLastRow) {
                        actions.push(
                            <GridActionsCellItem
                                key="add"
                                icon={<AddIcon color='action'/>}
                                label={t('general.buttons.add')}
                                onClick={addLimit}
                                showInMenu={false}
                            />
                        );
                    }
                    return actions;
                },
                resizable: false, filterable: false, sortable: false,
                editable: false, groupable: false,

            },
            {
                field: 'limit_id',
                headerName: t('accruals.accrual_rules.dialog.sections.limits'),
                headerClassName: 'super-app-theme--header',
                headerAlign: 'center',
                flex: 1.5,
                align: 'center',
                renderCell: (params: any) => (
                    <LimitSelectCell
                        rowIndex={params.row._index}
                        control={control}
                        loading={loadingStates.limits}
                        limits={availableData.limits}
                        t={t}
                        relationsData={relationsData}
                        setRelationsData={setRelationsData}
                    />
                ),
                resizable: false, filterable: false, sortable: false,
                editable: false, groupable: false,
            },
            {
                field: 'limit_amount',
                headerName: t('accruals.accrual_rules.dialog.inputs.amount'),
                headerClassName: 'super-app-theme--header',
                flex: 1,
                align: 'center',
                renderCell: (params: any) => (
                    <AmountCell
                        rowIndex={params.row._index}
                        type="limit"
                        t={t}
                        relationsData={relationsData}
                        setRelationsData={setRelationsData}
                        unit={selectedTypeAccrual}
                    />
                ),
                resizable: false, filterable: false, sortable: false,
                editable: false, groupable: false,
            },
        ],
        [t, availableData.limits, limitRows.length, control, loadingStates.limits, relationsData, setRelationsData, addLimit, removeLimit, selectedTypeAccrual],
    );


    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                {t('accruals.accrual_rules.dialog.titles.basic')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                {/* -------- name -------- */}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { onChange, value, onBlur } }) => (
                            <TextField
                                fullWidth
                                label={t('accruals.accrual_rules.dialog.inputs.name')}
                                value={value}
                                autoComplete='off'
                                onChange={(e) => {
                                    if (nameValidation(e.target.value)) {
                                        onChange(e.target.value);
                                        setShowExistsError(false);
                                    }
                                }}
                                onBlur={(e) => {
                                    onBlur();
                                    if (nameValue !== '' && existName(nameValue)) {
                                        setShowExistsError(true);
                                    }
                                }}
                                error={!!errors.name || (showExistsError && existName(nameValue) && nameValue !== '')}
                                helperText={
                                    errors.name?.message ||
                                    (showExistsError && existName(nameValue) && nameValue !== '' ? 'El nombre ya existe' : '')
                                }
                                slotProps={{
                                    htmlInput: { maxLength: 100 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${watch('name').length}/100`}</InputAdornment>,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>

                {/* -------- description -------- */}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label={t('accruals.accrual_rules.dialog.inputs.description')}
                                {...field}
                                value={field.value || ''}
                                autoComplete='off'
                                slotProps={{
                                    htmlInput: { maxLength: 255 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${field.value?.length}/255`}</InputAdornment>,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>

                {/* -------- accrual code -------- */}
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Controller
                        name="accrualCodeId"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={selectedAccrualCode?.type_accrual
                                    ? availableData.accrualCodes.filter(code => code.type_accrual === selectedAccrualCode.type_accrual)
                                    : availableData.accrualCodes}
                                getOptionLabel={(option) => `${option.name}  - (${option.type_accrual.toLocaleLowerCase()})`}
                                value={availableData.accrualCodes.find(code => code.accrual_code_id === field.value) || null}
                                onChange={(event: any, newValue) => {
                                    field.onChange(newValue ? newValue.accrual_code_id : '');
                                    const selectedTypeAccrual = newValue?.type_accrual ?? '';
                                    setRelationsData(prev => ({
                                        ...prev,
                                        limits: prev.limits.map(limit => ({
                                            ...limit,
                                            limit_amount_unit: selectedTypeAccrual,
                                        })),
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('accruals.accrual_rules.dialog.inputs.accrual_code')}
                                        fullWidth
                                        required
                                        error={!!errors.accrualCodeId || (showExistsError && existName(nameValue) && nameValue !== '')}
                                        helperText={
                                            errors.accrualCodeId?.message ||
                                            (showExistsError && existName(nameValue) && nameValue !== '' ? 'El nombre ya existe' : '')
                                        }
                                        disabled={loadingStates.accrualCodes}
                                        slotProps={{
                                            input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                {selectedAccrualCode?.type_accrual && (
                                                    <Chip
                                                        label={selectedAccrualCode.type_accrual}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ mr: 0.5 }}
                                                    />
                                                )}
                                                {loadingStates.accrualCodes ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                                </>
                                            ),
                                            },
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>

                {/* -------- reference date service -------- */}
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Controller
                        name="referenceDateServiceId"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={availableData.datePatterns}
                                getOptionLabel={(option) => option.name}
                                value={availableData.datePatterns.find(dp => dp.date_pattern_id === field.value) || null}
                                onChange={(event: any, newValue) => {
                                    field.onChange(newValue ? newValue.date_pattern_id : null);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('accruals.accrual_rules.dialog.inputs.reference_date_service')}
                                        fullWidth
                                        disabled={loadingStates.datePatterns}
                                        slotProps={{
                                            input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                {loadingStates.datePatterns ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                                </>
                                            ),
                                            },
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>

                {/* -------- accrual surplus -------- */}
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Controller
                        name="accrualSurplusId"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={availableData.accrualCodes.filter(code => code.type_accrual !== TipoAcumulacionEnum.DINERO)}
                                getOptionLabel={(option) => option.name}
                                value={availableData.accrualCodes.filter(code => code.type_accrual !== TipoAcumulacionEnum.DINERO).find(code => code.accrual_code_id === field.value) ?? null}
                                onChange={(event: any, newValue) => {
                                    field.onChange(newValue ? newValue.accrual_code_id : null);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('accruals.accrual_rules.dialog.inputs.accrual_surplus')}
                                        fullWidth
                                        disabled={loadingStates.accrualCodes}
                                        slotProps={{
                                            input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                {loadingStates.accrualCodes ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                                </>
                                            ),
                                            },
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>

                {/* -------- adjust balance -------- */}
                <Grid size={{ xs: 12 }}>
                    <Controller
                        name="adjustBalance"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={value}
                                        onChange={(e) => onChange(e.target.checked)}
                                    />
                                }
                                label={t('accruals.accrual_rules.dialog.inputs.adjust_balance')}
                            />
                        )}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* ── Grants ──────────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>
                {t('accruals.accrual_rules.dialog.sections.grants')}
            </Typography>
            {loadingStates.grants ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box
                    sx={{
                        maxHeight: 300,
                        width: '100%',
                        overflow: 'auto',
                        '& .super-app-theme--header': {
                            backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
                        },
                    }}
                >
                    <DataGrid
                        rows={grantRows}
                        columns={grantColumns}
                        density="compact"
                        disableColumnMenu
                        disableRowSelectionOnClick
                        hideFooter
                        hideFooterPagination
                        hideFooterSelectedRowCount
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        rowHeight={80}
                        autosizeOptions={{ outliersFactor: 0.5 }}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                textAlign: 'center',
                                height: 'auto',
                            },
                            '& .MuiDataGrid-row': {
                                transition: 'all 0.3s ease-in-out',
                            },
                            '& .MuiDataGrid-bordered': {
                                /* borderColor: theme.palette.divider, */
                                borderColor: theme.palette.mode === 'light' ? '#e0e0e0' : '#616161',
                            }
                        }}
                    />
                </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* ── Limits ──────────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>
                {t('accruals.accrual_rules.dialog.sections.limits')}
            </Typography>
            {loadingStates.limits ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box
                    sx={{
                        maxHeight: 300,
                        width: '100%',
                        overflow: 'auto',
                        '& .super-app-theme--header': {
                            backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
                        },
                    }}
                >
                    <DataGrid
                        rows={limitRows}
                        columns={limitColumns}
                        density="compact"
                        disableColumnMenu
                        disableRowSelectionOnClick
                        hideFooter
                        hideFooterPagination
                        hideFooterSelectedRowCount
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        rowHeight={80}
                        autosizeOptions={{ outliersFactor: 0.5 }}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                textAlign: 'center',
                                height: 'auto',
                            },
                            '& .MuiDataGrid-row': {
                                transition: 'all 0.3s ease-in-out',
                            },
                        }}
                    />
                </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                {t('accruals.accrual_rules.dialog.sections.use_balance')}
            </Typography>
            <ListTransfer
                loading={loadingStates.paycodes}
                allItems={availableData.paycodes.map(p => ({ id: p.paycode_id, name: p.name }))}
                right={relationsData.useBalances.map(ub => {
                    const paycode = availableData.paycodes.find(p => p.paycode_id === ub.paycode_id);
                    return { id: ub.paycode_id, name: paycode?.name || '' };
                })}
                setRight={(newRight: { id: number; name: string }[]) => {
                    const newUseBalances = newRight.map((item: { id: number; name: string }) => ({ paycode_id: item.id }));
                    setUseBalances(newUseBalances);
                }}
                textLeft={t('accruals.accrual_rules.dialog.sections.available')}
                textRight={t('accruals.accrual_rules.dialog.sections.selected')}
                textSelected={t('accruals.accrual_rules.dialog.sections.selected')}
            />
        </Box>
    );
};
